import { useEffect } from 'react';

import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import exerciseQueuesApi, {
  exerciseQueuesApiKeys,
  exerciseQueuesAvailabilityQueryOptions,
} from 'api/rest/exerciseQueues/exerciseQueues';
import { ON_GO_ACCESS } from 'constants/onGoAccess.config';
import PATHS from 'constants/paths';
import useOnGoAccess from 'hooks/useOnGoAccess/useOnGoAccess';
import useSubjectStore from 'storages/subject';

export const getOnGoAdditionalText = (
  onGoAvailability?: { available: boolean } | null,
  isDailyLimitReached = false,
): string | null => {
  if (onGoAvailability && !onGoAvailability.available) {
    return 'Niedostepne dla tego przedmiotu';
  }

  if (isDailyLimitReached) {
    return ON_GO_ACCESS.copy.dashboard.premiumBadge;
  }

  return null;
};

const useOnGoAvailability = () => {
  const location = useLocation();
  const { subject } = useSubjectStore();
  const subjectPk = subject?.pk;
  const { canUseOnGo, refetchFeatures } = useOnGoAccess({ refetchOnWindowFocus: true });
  const isAvailabilityQueryEnabled = subjectPk != null;

  useEffect(() => {
    if (location.pathname === PATHS.ROOT) {
      refetchFeatures();
    }
  }, [location.pathname, refetchFeatures]);

  const { data: onGoAvailability } = useQuery(
    isAvailabilityQueryEnabled ? exerciseQueuesApiKeys.availability(subjectPk) : ['exerciseQueues', 'availability', 'disabled'],
    () => {
      if (subjectPk == null) {
        throw new Error('subjectPk is required');
      }

      return exerciseQueuesApi.getAvailability(subjectPk)();
    },
    {
      enabled: isAvailabilityQueryEnabled,
      ...exerciseQueuesAvailabilityQueryOptions,
    },
  );

  const isOnGoUnavailableForSubject = Boolean(onGoAvailability && !onGoAvailability.available);
  const isOnGoDailyLimitReached = !canUseOnGo;
  const onGoAdditionalText = getOnGoAdditionalText(onGoAvailability, isOnGoDailyLimitReached);
  const isOnGoDisabled = isOnGoUnavailableForSubject || isOnGoDailyLimitReached;

  return {
    onGoAvailability,
    isOnGoUnavailableForSubject,
    isOnGoDailyLimitReached,
    onGoAdditionalText,
    isOnGoDisabled,
  };
};

export default useOnGoAvailability;
